import React from "react";
import TooltippedElement from "components/TooltippedElement";
import { IconButton } from "components/button";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { FaPlus, FaUndo } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";

type Props = {
  id: number;
  label?: string | null;
  color?: string | null;
  archived?: boolean;
  isExpanded?: boolean;
  showExpand?: boolean;
  defaultsCount?: number;
  nested?: boolean;
  defaultsLabel?: string;
  archiveLabel?: string;
  restoreLabel?: string;
  editLabel?: string;
  addDefaultLabel?: string;
  onToggleExpand?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onEdit?: () => void;
  onAddDefault?: () => void;
};

const iconCircleStyle =
  " cursor-pointer py-2 h-10 w-10 rounded-full bg-[#edf1f4] flex items-center justify-center hover:bg-[#bec1c3] select-none";

const AppointmentRow = ({
  id,
  label,
  color,
  archived = false,
  isExpanded = false,
  showExpand = false,
  defaultsCount,
  nested = false,
  defaultsLabel = "defaults",
  archiveLabel = "Archive",
  restoreLabel = "Restore",
  editLabel = "Edit",
  addDefaultLabel = "Add Default",
  onToggleExpand,
  onArchive,
  onRestore,
  onEdit,
  onAddDefault,
}: Props) => {
  return (
    <div
      className={`
        p-2 pr-4 flex items-center justify-between border rounded-md shadow-md
        ${archived ? "bg-gray-50" : "bg-white"}
        ${isExpanded ? "rounded-b-none border-b-0" : ""}
        ${nested ? "mx-2" : ""}
      `}
    >
      <div className={`
        flex items-center flex-1 
        ${nested ? "-ml-5" : ""}
      `}>
        {showExpand ? (
          <button
            onClick={onToggleExpand}
            className="ml-2 p-1 hover:bg-gray-100 rounded"
          >
            {isExpanded ? (
              <IoIosArrowDown size={18} className="text-gray-600" />
            ) : (
              <IoIosArrowForward size={18} className="text-gray-600" />
            )}
          </button>
        ) : (
          <div className="mr-2 w-[26px]" />
        )}

        <h4 className="mr-4 pr-2 pt-2 pb-2 border-r-2 border-slate-300 text-center w-[60px] align-middle">
          {id}
        </h4>

        <div className="mr-4 pr-4 pt-2 pb-2 border-r-2 border-slate-300 text-center align-middle">
          {color ? (
            <div
              style={{ backgroundColor: color }}
              className="w-5 h-5 rounded-full"
            ></div>
          ) : (
            <div className="relative bg-white h-5 w-5 rounded-full overflow-hidden border">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-px h-7 bg-red-500"></div>
            </div>
          )}
        </div>

        <h4>{label || ""}</h4>

        {typeof defaultsCount === "number" && defaultsCount > 0 && (
          <div className="pt-1 pb-2 ml-3 border-l-2 border-slate-300 pl-3">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
              {defaultsCount} {defaultsLabel}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-start -mb-2 space-x-3">
        {archived ? (
          onRestore ? (
            <TooltippedElement
              placement="top"
              message={restoreLabel}
              className="inline-flex"
              element={
                <IconButton onClick={onRestore}>
                  <FaUndo
                    size={24}
                    className="text-green-500 p-0.5 cursor-pointer"
                  />
                </IconButton>
              }
            />
          ) : null
        ) : (
          onArchive ? (
            <TooltippedElement
              placement="top"
              message={archiveLabel}
              className="inline-flex"
              element={
                <IconButton onClick={onArchive}>
                  <AiFillDelete size={24} className="text-red-500 cursor-pointer" />
                </IconButton>
              }
            />
          ) : null
        )}
        {onEdit ? (
          <TooltippedElement
            placement="top"
            message={editLabel}
            element={
              <button onClick={onEdit}>
                <AiFillEdit size={24} className={"text-blue-500" + iconCircleStyle} />
              </button>
            }
          />
        ) : null}
        {onAddDefault ? (
          <TooltippedElement
            placement="top"
            message={addDefaultLabel}
            element={
              <IconButton onClick={onAddDefault}>
                <FaPlus size={22} className="text-emerald-600 p-0.5 cursor-pointer" />
              </IconButton>
            }
          />
        ) : null}
      </div>
    </div>
  );
};

export { AppointmentRow };
